import styled, { css, DefaultTheme, SimpleInterpolation, ThemedStyledProps } from 'styled-components';

import { basicCaptionTextStyle, basicTransition, fonts } from '@dreryk/react-components';

import { ReimbursementVariant } from './Reimbursement.types';

export const StyledReimbursementIcon = styled.div`
   fill: ${(props) => props.theme.primary};
   display: none;
   position: absolute;
   top: 2px;
   right: 4px;
   bottom: 0;
   height: 16px;
   width: 16px;
`;
StyledReimbursementIcon.displayName = 'StyledReimbursementIcon';

export const StyledReimbursementCode = styled.div<{ $clickable: boolean }>`
   color: ${(props) => (props.$clickable ? props.theme.primary : props.theme.textBody)};
   margin-right: 4px;
   font-weight: ${fonts.fontWeightBold};
`;
StyledReimbursementCode.displayName = 'StyledReimbursementCode';

export const StyledReimbursementPriceDescription = styled.div`
   font-weight: ${fonts.fontWeightNormal};
`;
StyledReimbursementPriceDescription.displayName = 'StyledReimbursementPriceDescription';

type StyledReimbursementProps = {
   $variant: ReimbursementVariant;
   $active: boolean | undefined;
   $icon: boolean | undefined;
   disabled: boolean | undefined;
   $clickable: boolean | undefined;
   $distinct: boolean | undefined;
};

type ReimbursementComponentState = {
   hover: boolean;
};

const variantResolver = <PRIMARY, STANDARD, PROPS extends { $variant: ReimbursementVariant }>(
   primary: PRIMARY,
   standard: STANDARD
) => (props: ThemedStyledProps<PROPS, DefaultTheme>): PRIMARY | STANDARD => {
   if (props.$variant === ReimbursementVariant.PRIMARY) {
      return primary;
   }

   return standard;
};

const resolveBackgroundColor = (state: ReimbursementComponentState) => (
   props: ThemedStyledProps<StyledReimbursementProps, DefaultTheme>
): string => {
   if (state.hover || props.$active) {
      return variantResolver(props.theme.primaryBackground, props.theme.backgroundBody)(props);
   }

   return 'transparent';
};

const resolveReimbursementPriceDescriptionHover = (): SimpleInterpolation => {
   return css`
      opacity: 1;
   `;
};

const resolveStyledReimbursementIcon = (
   props: ThemedStyledProps<StyledReimbursementProps, DefaultTheme>
): SimpleInterpolation => {
   if (props.$active) {
      return css`
         display: block;
      `;
   }

   return css`
      display: none;
   `;
};

const resolveReimbursementPriceDescription = (
   props: ThemedStyledProps<StyledReimbursementProps, DefaultTheme>
): SimpleInterpolation | null => {
   if (props.$icon) {
      return css`
         margin-right: 16px;
      `;
   }

   return null;
};

const resolveReimbursementPriceDescriptionDisabled = (
   props: ThemedStyledProps<StyledReimbursementProps, DefaultTheme>
): SimpleInterpolation => {
   return css`
      color: ${props.theme.disabledDarker};
   `;
};

const resolveReimbursementCodeDisabled = (
   props: ThemedStyledProps<StyledReimbursementProps, DefaultTheme>
): SimpleInterpolation => {
   return css`
      color: ${props.theme.textBody};
   `;
};

export const StyledReimbursement = styled.div<StyledReimbursementProps>`
   ${basicTransition}
   ${basicCaptionTextStyle}
   position: relative;
   display: flex;
   width: fit-content;
   padding: 5px 6px 4px 6px;
   border-radius: 16px;
   background-color: ${resolveBackgroundColor({ hover: false })};
   user-select: none;
   pointer-events: none;

   ${(props) =>
      props.$distinct &&
      css`
         border: 1px dashed ${props.theme.primary};
      `}

   ${StyledReimbursementPriceDescription} {
      ${resolveReimbursementPriceDescription}
   }

   ${StyledReimbursementIcon} {
      ${resolveStyledReimbursementIcon}
   }

   ${(props) =>
      props.$clickable &&
      props.$icon &&
      !props.disabled &&
      css`
         pointer-events: auto;

         &:hover {
            background-color: ${resolveBackgroundColor({ hover: true })};
            min-width: 40px;
            cursor: pointer;

            ${StyledReimbursementPriceDescription} {
               ${resolveReimbursementPriceDescriptionHover}
            }

            ${StyledReimbursementIcon} {
               display: block;
            }
         }
      `}

   ${(props) =>
      props.disabled &&
      css`
         ${StyledReimbursementCode} {
            ${resolveReimbursementCodeDisabled}
         }

         ${StyledReimbursementPriceDescription} {
            ${resolveReimbursementPriceDescriptionDisabled}
         }
      `}
`;
StyledReimbursement.displayName = 'StyledReimbursement';
